import { getApi, postApi } from "./resonanceApi";
import { AxiosResponse } from 'axios';
import { InvitationConfigType, PostDirectInclusionRequestType, SendInvitationBodyType, SendInvitationParamsType } from "../types/invitation.types";

export enum InvitationUrlsEnum {
  GET_INVITATIONS = 'studies/{studyId}/users/current/invitations/configs',
  POST_INVITATION = 'studies/{study_id}/users/current/invitations',
  GET_INVITATION_PUB_LINK = 'studies/{studyId}/users/current/invitations/link',
  DIRECT_INCLUSION = 'studies/{studyId}/users/current/invitations/direct_inclusion',
  POST_DIRECT_INCLUSION = 'studies/{studyId}/users/current/invitations/direct_inclusion_invitation',
}

export const getInvitations = ({ studyId, lang }): Promise<AxiosResponse<InvitationConfigType[]>> => {
  const url = InvitationUrlsEnum.GET_INVITATIONS;
  return getApi<InvitationConfigType[]>(url, { studyId, lang });
};

/**
 * Cached read of the caller's invitation configs, keyed by study + language.
 *
 * These decide whether the Compte page shows a « Partager » / « Inviter … » row,
 * so fetching them on page mount made both rows POP IN after first paint and shove
 * the rest of the card down — a visible jump every time the page opened. With the
 * cache warmed at app entry (see Tabs.tsx) the answer is already known when the
 * page paints, and `peekInvitationConfigs` lets a component seed its initial state
 * from it synchronously instead of starting at a guess.
 *
 * Per-USER data (it depends on the caller's role and what their dossier already
 * holds), so it is cleared on logout by resetCaches() — never let the next account
 * on this device inherit it.
 */
let invitationConfigCache: { [key: string]: InvitationConfigType[] } = {};
let invitationConfigTimestamps: { [key: string]: number } = {};
const INVITATION_CACHE_EXPIRY_TIME = 600000; // 10 minutes, like the questionnaire caches

const invitationCacheKey = (studyId: number, lang: string) => `${studyId}_${lang}`;

export const resetInvitationConfigCache = () => {
  invitationConfigCache = {};
  invitationConfigTimestamps = {};
};

/** Cached configs if fresh, else null. Synchronous — safe for useState initialisers. */
export const peekInvitationConfigs = (
  studyId: number | undefined,
  lang: string
): InvitationConfigType[] | null => {
  if (!studyId) return null;
  const key = invitationCacheKey(studyId, lang);
  const isFresh =
    Date.now() - (invitationConfigTimestamps[key] ?? 0) < INVITATION_CACHE_EXPIRY_TIME;
  return isFresh ? invitationConfigCache[key] ?? null : null;
};

export const getCachedInvitationConfigs = async (
  studyId: number,
  lang: string
): Promise<InvitationConfigType[]> => {
  const cached = peekInvitationConfigs(studyId, lang);
  if (cached) return cached;

  const { data } = await getInvitations({ studyId, lang });
  const configs = data ?? [];
  const key = invitationCacheKey(studyId, lang);
  invitationConfigCache[key] = configs;
  invitationConfigTimestamps[key] = Date.now();
  return configs;
};

export const sendInvitation = (params: SendInvitationParamsType, body: SendInvitationBodyType): Promise<AxiosResponse> => {
  const url = InvitationUrlsEnum.POST_INVITATION;
  return postApi(url, params, body);
}

export const getInvitPubLink = (params: { studyId: number }, body: { invitation_config_id: number }): Promise<AxiosResponse> => {
  const url = InvitationUrlsEnum.GET_INVITATION_PUB_LINK;
  return postApi(url, params, body);
};

export const getDirectInclusion = (paramas: { studyId: number }): Promise<AxiosResponse<string>> => {
  const url = InvitationUrlsEnum.DIRECT_INCLUSION;
  return getApi(url, paramas);
}

export const postDirectInclusion = (params: { studyId: number }, body: PostDirectInclusionRequestType): Promise<AxiosResponse> => {
  const url = InvitationUrlsEnum.POST_DIRECT_INCLUSION;
  return postApi(url, params, body);
}
